import * as THREE from 'three';

export class LightSource {
    constructor(position = new THREE.Vector3(10, 10, 10), intensity = 1, color = 0xffffff) {
        this.position = position.clone();
        this.intensity = intensity;
        this.color = typeof color === 'number' ? new THREE.Color(color) : color.clone();
        
        this.light = null;
        this.visual = null;
        this.group = new THREE.Group();
        this.id = Math.random().toString(36).substr(2, 9);
        
        this.init();
    }

    init() {
        // Create Three.js point light
        this.light = new THREE.PointLight(this.color, this.intensity, 1000);
        this.light.decay = 0; // No decay - light intensity remains constant with distance
        this.light.position.copy(this.position);
        this.light.castShadow = true;
        this.light.shadow.mapSize.width = 1024;
        this.light.shadow.mapSize.height = 1024;
        this.group.add(this.light);
        
        // Create visual representation (glowing sphere)
        const visualGeometry = new THREE.SphereGeometry(0.5, 16, 16);
        const visualMaterial = new THREE.MeshBasicMaterial({
            color: this.color,
            transparent: true,
            opacity: 0.8
        });
        this.visual = new THREE.Mesh(visualGeometry, visualMaterial);
        this.visual.position.copy(this.position);
        this.group.add(this.visual);
        
        // Add glow effect
        const glowGeometry = new THREE.SphereGeometry(1, 16, 16);
        const glowMaterial = new THREE.MeshBasicMaterial({
            color: this.color,
            transparent: true,
            opacity: 0.3,
            side: THREE.BackSide
        });
        const glow = new THREE.Mesh(glowGeometry, glowMaterial);
        glow.position.copy(this.position);
        this.group.add(glow);
    }

    setPosition(position) {
        this.position.copy(position);
        this.light.position.copy(position);
        this.visual.position.copy(position);
        this.group.children.forEach(child => {
            if (child !== this.light && child !== this.visual && child.position) {
                child.position.copy(position);
            }
        });
    }

    setIntensity(intensity) {
        this.intensity = intensity;
        if (this.light) {
            this.light.intensity = intensity;
        }
        
        // Update visual representation to reflect intensity
        if (this.visual && this.visual.material) {
            // Scale opacity based on intensity (normalize to 0-50 range, cap at 1.0)
            const normalizedIntensity = Math.min(intensity / 50, 1.0);
            this.visual.material.opacity = 0.3 + normalizedIntensity * 0.7; // Range from 0.3 to 1.0
            // Also scale the sphere size slightly
            const scale = 0.5 + normalizedIntensity * 0.5; // Range from 0.5 to 1.0
            this.visual.scale.setScalar(scale);
        }
        
        // Update glow effect too
        this.group.children.forEach(child => {
            if (child !== this.light && child !== this.visual && child.material) {
                const normalizedIntensity = Math.min(intensity / 50, 1.0);
                child.material.opacity = 0.1 + normalizedIntensity * 0.4; // Range from 0.1 to 0.5
                const scale = 1.0 + normalizedIntensity * 0.5; // Range from 1.0 to 1.5
                if (child.scale) {
                    child.scale.setScalar(scale);
                }
            }
        });
    }

    setColor(color) {
        this.color = typeof color === 'number' ? new THREE.Color(color) : color.clone();
        this.light.color.copy(this.color);
        this.visual.material.color.copy(this.color);
        this.group.children.forEach(child => {
            if (child !== this.light && child !== this.visual && child.material) {
                child.material.color.copy(this.color);
            }
        });
    }

    getPosition() {
        return this.position.clone();
    }

    getIntensity() {
        return this.intensity;
    }

    getColor() {
        return this.color.clone();
    }

    getLight() {
        return this.light;
    }

    getGroup() {
        return this.group;
    }

    getId() {
        return this.id;
    }

    update(deltaTime) {
        // Optional: pulsing effect
        const pulse = 1 + Math.sin(Date.now() * 0.001) * 0.1;
        this.visual.scale.setScalar(pulse);
    }

    dispose() {
        if (this.visual) {
            this.visual.geometry.dispose();
            this.visual.material.dispose();
        }
        this.group.children.forEach(child => {
            if (child !== this.light) {
                if (child.geometry) child.geometry.dispose();
                if (child.material) child.material.dispose();
            }
        });
        // Light doesn't need disposal in Three.js
    }
}
